// VIN (Vehicle Identification Number) validation utilities.
// Implements the ISO 3779 / SAE J272 check digit algorithm used by all
// VINs issued in North America since 1981 (mandatory) and by most European
// manufacturers (optional but commonly respected).
//
// References:
//   - ISO 3779 (VIN content/structure)
//   - 49 CFR § 565 (NHTSA, USA)
//   - SAE J272 (check-digit algorithm)

// Char → numeric value table used for the weighted sum.
// I, O and Q are NOT valid in a VIN (they are visually ambiguous).
const CHAR_VALUE = {
  '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
  'A': 1, 'B': 2, 'C': 3, 'D': 4, 'E': 5, 'F': 6, 'G': 7, 'H': 8,
  'J': 1, 'K': 2, 'L': 3, 'M': 4, 'N': 5, 'P': 7, 'R': 9,
  'S': 2, 'T': 3, 'U': 4, 'V': 5, 'W': 6, 'X': 7, 'Y': 8, 'Z': 9,
};
// Weights per position (1-indexed), with position 9 = 0 because that
// position IS the check digit.
const POSITION_WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

const FORBIDDEN_CHARS = /[IOQ]/;

/**
 * Validate a VIN against the ISO 3779 / SAE J272 algorithm + heuristics.
 *
 * Returns an object with shape:
 *   {
 *     valid: bool,                  // overall verdict
 *     severity: 'ok' | 'warn' | 'error',
 *     code: 'OK' | 'OK_NON_NA' | 'BAD_LENGTH' | 'FORBIDDEN_CHAR' | 'CHECKSUM_FAIL' | 'SUSPICIOUS_PATTERN',
 *     message: string,              // human-readable, italian (translated upstream)
 *     details: { ... }              // diagnostic details
 *   }
 *
 * Notes:
 *   - North American VINs (1st char in "1245") MUST pass the check digit.
 *     If they don't → severity 'error', valid false.
 *   - Non-NA VINs (European, Asian, etc.) → checksum is informative. If
 *     the algorithm fails we return 'warn' (the VIN may still be valid
 *     because outside North America it's not mandatory), but if the algo
 *     PASSES we upgrade to 'ok'.
 *   - Pattern check rejects VINs with >=7 identical chars in a row.
 */
export function validateVin(rawVin) {
  const vin = String(rawVin || '').toUpperCase().replace(/\s+/g, '');

  // Length check
  if (vin.length !== 17) {
    return {
      valid: false, severity: 'error', code: 'BAD_LENGTH',
      message: `VIN deve essere di 17 caratteri (${vin.length}/17)`,
      details: { length: vin.length },
    };
  }

  // Forbidden chars
  if (FORBIDDEN_CHARS.test(vin)) {
    return {
      valid: false, severity: 'error', code: 'FORBIDDEN_CHAR',
      message: 'VIN contiene caratteri non consentiti (I, O, Q)',
      details: { found: vin.match(FORBIDDEN_CHARS)?.[0] },
    };
  }

  // Heuristic: detect long runs of identical chars (a strong sign of a fake VIN)
  const runMatch = vin.match(/(.)\1{6,}/);  // 7+ identical in a row
  if (runMatch) {
    return {
      valid: false, severity: 'warn', code: 'SUSPICIOUS_PATTERN',
      message: `VIN sospetto: ${runMatch[0].length} caratteri identici consecutivi ("${runMatch[1]}")`,
      details: { run: runMatch[0] },
    };
  }

  // Compute weighted sum for the check digit
  let sum = 0;
  for (let i = 0; i < 17; i++) {
    const ch = vin[i];
    const val = CHAR_VALUE[ch];
    if (val === undefined) {
      // Should not happen since we filtered forbidden chars; keep defensive
      return {
        valid: false, severity: 'error', code: 'FORBIDDEN_CHAR',
        message: `Carattere non valido in posizione ${i + 1}: "${ch}"`,
        details: { position: i + 1, char: ch },
      };
    }
    sum += val * POSITION_WEIGHTS[i];
  }
  const remainder = sum % 11;
  const expected = remainder === 10 ? 'X' : String(remainder);
  const actual = vin[8];  // 9th position (0-indexed)
  const passes = expected === actual;

  // Is this a North American VIN? (1st digit indicates region)
  // 1, 4, 5 = USA;  2 = Canada;  3 = Mexico
  const isNorthAmerican = '12345'.includes(vin[0]);

  if (passes) {
    return {
      valid: true, severity: 'ok', code: 'OK',
      message: 'VIN valido (checksum OK)',
      details: { expected, actual, region: isNorthAmerican ? 'NA' : 'other' },
    };
  }

  // Checksum FAILED
  if (isNorthAmerican) {
    return {
      valid: false, severity: 'error', code: 'CHECKSUM_FAIL',
      message: `Checksum VIN non valido: la cifra in posizione 9 dovrebbe essere "${expected}" ma è "${actual}"`,
      details: { expected, actual, region: 'NA' },
    };
  }

  // Outside North America: many real VINs do not enforce the check digit.
  // Downgrade to a soft warning instead of a hard fail.
  return {
    valid: true, severity: 'warn', code: 'OK_NON_NA',
    message: `Formato VIN valido, ma il check digit non corrisponde (atteso "${expected}", trovato "${actual}"). Comune nei veicoli europei.`,
    details: { expected, actual, region: 'non-NA' },
  };
}

export default validateVin;
